import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { fadeUp, stagger } from "@/lib/motion";

/**
 * Homepage introduction for The Rhythm Report (Morgan's music vertical).
 * CGD-pink section with a black-and-white preview card so visitors see
 * exactly what they're clicking into.
 */
export default function RhythmIntro() {
  return (
    <section
      id="rhythm-intro"
      data-testid="rhythm-intro-section"
      className="scroll-mt-24 py-24 lg:py-32"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <motion.div
          variants={stagger}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.2 }}
          className="grid items-center gap-10 lg:grid-cols-2 lg:gap-16"
        >
          <motion.div variants={fadeUp}>
            <p className="mb-4 text-[11px] font-bold uppercase tracking-[0.24em] text-cherry">
              More from the house
            </p>
            <h2 className="font-display text-3xl font-bold tracking-tight sm:text-4xl lg:text-5xl">
              Need the music takes too?{" "}
              <span className="font-garamond italic text-cherry">
                The Rhythm Report.
              </span>
            </h2>
            <p className="mt-6 max-w-lg text-base leading-relaxed text-white/65 sm:text-lg">
              Morgan's music vertical under the CGD umbrella — unfiltered album
              reviews scored in spins, a live board tracking who's actually
              charting, and artist spotlights you hear here first. Same voice,
              black-and-white pages.
            </p>
            <Link
              data-testid="rhythm-intro-cta"
              to="/rhythm"
              className="mt-8 inline-flex items-center gap-3 rounded-full bg-cherry px-7 py-4 text-sm font-bold uppercase tracking-[0.12em] text-white transition-all duration-300 hover:scale-[1.04] hover:bg-cherry-soft hover:shadow-[0_0_40px_rgba(255,0,85,0.45)]"
            >
              Read the latest issue <ArrowRight className="h-4 w-4" />
            </Link>
          </motion.div>

          <motion.div variants={fadeUp}>
            <Link
              data-testid="rhythm-intro-card"
              to="/rhythm"
              className="group block overflow-hidden rounded-3xl border border-white/10 bg-[#0B0A0F] transition-transform duration-300 hover:scale-[1.02]"
            >
              <img
                src="/images/kmichelle-xxl-cover-v1.jpg"
                alt="The Rhythm Report Issue 03 — K. Michelle cover story"
                className="h-64 w-full object-cover object-top sm:h-80"
              />
              <div className="p-8 sm:p-10">
                <p className="font-rhythm-mono text-[10px] uppercase tracking-[0.3em] text-white/55">
                  The Rhythm Report · Issue 03 · Cover Story
                </p>
                <h3 className="mt-4 font-rhythm-display text-2xl uppercase leading-[0.95] text-[#F3F1ED] sm:text-3xl">
                  K. Michelle — She Went Country
                </h3>
                <p className="mt-4 max-w-sm font-rhythm text-sm leading-relaxed text-white/60">
                  Jesus &amp; Whiskey is here: the R&amp;B girl's debut country
                  album, the FAMU yodel, and a reclamation Nashville didn't see
                  coming. Plus the First Listen package and MaliGotBandz.
                </p>
                <div className="mt-7 flex items-center justify-between border-t border-white/15 pt-5">
                  <span className="flex h-4 items-end gap-[3px]" aria-hidden="true">
                    {[0, 1, 2, 3, 4].map((i) => (
                      <span
                        key={i}
                        className="w-[3px] bg-[#F3F1ED]/70"
                        style={{ height: `${40 + i * 15}%` }}
                      />
                    ))}
                  </span>
                  <span className="font-rhythm-mono text-[10px] uppercase tracking-[0.22em] text-white/55">
                    $4.95 US · read free online
                  </span>
                  <span className="inline-flex items-center gap-2 font-rhythm-mono text-[10px] font-bold uppercase tracking-[0.22em] text-[#F3F1ED] transition-transform duration-300 group-hover:translate-x-1">
                    Open <ArrowRight className="h-3.5 w-3.5" />
                  </span>
                </div>
              </div>
            </Link>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
